import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: (data) => axios.post(`${API_URL}/auth/login`, data),
  register: (data) => axios.post(`${API_URL}/auth/register`, data),
  getMe: () => axios.get(`${API_URL}/auth/me`, { headers: getHeaders() }),

  // Properties
  getProperties: (params) => axios.get(`${API_URL}/properties`, { params }),
  getProperty: (id) => axios.get(`${API_URL}/properties/${id}`),
  createProperty: (data) => axios.post(`${API_URL}/properties`, data, { headers: getHeaders() }),
  getVendorProperties: () => axios.get(`${API_URL}/vendor/properties`, { headers: getHeaders() }),

  // Bookings
  createBooking: (data) => axios.post(`${API_URL}/bookings`, data, { headers: getHeaders() }),
  getBookings: () => axios.get(`${API_URL}/bookings`, { headers: getHeaders() }),
  updateBookingStatus: (id, status) => axios.patch(`${API_URL}/bookings/${id}/status?status=${status}`, {}, { headers: getHeaders() }),

  // Gifts
  getGifts: (params) => axios.get(`${API_URL}/gifts`, { params }),
  createGift: (data) => axios.post(`${API_URL}/gifts`, data, { headers: getHeaders() }),
  createGiftOrder: (data) => axios.post(`${API_URL}/gift-orders`, data, { headers: getHeaders() }),
  getGiftOrders: () => axios.get(`${API_URL}/gift-orders`, { headers: getHeaders() }),
  
  // Customized Gift Orders (Vistaprint-style)
  uploadLogo: (formData) => axios.post(`${API_URL}/upload/logo`, formData, { 
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } 
  }),
  createCustomizedGiftOrder: (data) => axios.post(`${API_URL}/customized-gift-orders`, data, { headers: getHeaders() }),
  getCustomizedGiftOrders: () => axios.get(`${API_URL}/customized-gift-orders`, { headers: getHeaders() }),
  getCustomizedGiftOrder: (orderId) => axios.get(`${API_URL}/customized-gift-orders/${orderId}`, { headers: getHeaders() }),

  // Reviews
  getReviews: (propertyId) => axios.get(`${API_URL}/reviews/${propertyId}`),
  createReview: (data) => axios.post(`${API_URL}/reviews`, data, { headers: getHeaders() }),

  // Permissions
  getPermissions: (params) => axios.get(`${API_URL}/permissions`, { params, headers: getHeaders() }),
  createPermission: (data) => axios.post(`${API_URL}/permissions`, data, { headers: getHeaders() }),

  // Payments - Razorpay
  createRazorpayOrder: (data) => axios.post(`${API_URL}/payments/create-order`, data, { headers: getHeaders() }),
  verifyRazorpayPayment: (data) => axios.post(`${API_URL}/payments/verify`, data, { headers: getHeaders() }),
  createCheckout: (data) => axios.post(`${API_URL}/payments/checkout`, data, { headers: getHeaders() }),
  getPaymentStatus: (sessionId) => axios.get(`${API_URL}/payments/status/${sessionId}`, { headers: getHeaders() }),

  // Trainer Bookings
  createTrainerBooking: (data) => axios.post(`${API_URL}/trainer-bookings`, data, { headers: getHeaders() }),
  getTrainerBookings: () => axios.get(`${API_URL}/trainer-bookings`, { headers: getHeaders() }),
  getTrainerBooking: (id) => axios.get(`${API_URL}/trainer-bookings/${id}`, { headers: getHeaders() }),
  updateTrainerBookingStatus: (id, status) => axios.patch(`${API_URL}/trainer-bookings/${id}/status?status=${status}`, {}, { headers: getHeaders() }),
  createTrainerBookingPayment: (bookingId, customAmount = null) => {
    const url = customAmount ? `${API_URL}/trainer-bookings/${bookingId}/pay?custom_amount=${customAmount}` : `${API_URL}/trainer-bookings/${bookingId}/pay`;
    return axios.post(url, {}, { headers: getHeaders() });
  },
  verifyTrainerBookingPayment: (bookingId, data) => axios.post(`${API_URL}/trainer-bookings/${bookingId}/verify-payment`, data, { headers: getHeaders() }),

  // Blog
  getBlogs: (params) => axios.get(`${API_URL}/blogs`, { params }),
  createBlog: (data) => axios.post(`${API_URL}/blogs`, data, { headers: getHeaders() }),

  // Testimonials
  getTestimonials: () => axios.get(`${API_URL}/testimonials`),
  createTestimonial: (data) => axios.post(`${API_URL}/testimonials`, data, { headers: getHeaders() }),

  // Itineraries
  getItineraries: () => axios.get(`${API_URL}/itineraries`, { headers: getHeaders() }),
  getItinerary: (id) => axios.get(`${API_URL}/itineraries/${id}`, { headers: getHeaders() }),
  createItinerary: (data) => axios.post(`${API_URL}/itineraries`, data, { headers: getHeaders() }),
  updateItinerary: (id, items) => axios.patch(`${API_URL}/itineraries/${id}`, items, { headers: getHeaders() }),
  
  // Itinerary - Guests
  addGuest: (itineraryId, guest) => axios.post(`${API_URL}/itineraries/${itineraryId}/guests`, guest, { headers: getHeaders() }),
  bulkAddGuests: (itineraryId, guests) => axios.post(`${API_URL}/itineraries/${itineraryId}/guests/bulk`, guests, { headers: getHeaders() }),
  updateGuest: (itineraryId, guestId, guest) => axios.patch(`${API_URL}/itineraries/${itineraryId}/guests/${guestId}`, guest, { headers: getHeaders() }),
  deleteGuest: (itineraryId, guestId) => axios.delete(`${API_URL}/itineraries/${itineraryId}/guests/${guestId}`, { headers: getHeaders() }),
  
  // Itinerary - Rooms
  addRoom: (itineraryId, room) => axios.post(`${API_URL}/itineraries/${itineraryId}/rooms`, room, { headers: getHeaders() }),
  updateRooms: (itineraryId, rooms) => axios.put(`${API_URL}/itineraries/${itineraryId}/rooms`, rooms, { headers: getHeaders() }),
  
  // Itinerary - Schedule
  addScheduleItem: (itineraryId, item) => axios.post(`${API_URL}/itineraries/${itineraryId}/schedule`, item, { headers: getHeaders() }),
  updateSchedule: (itineraryId, schedule) => axios.put(`${API_URL}/itineraries/${itineraryId}/schedule`, schedule, { headers: getHeaders() }),
  
  // Itinerary - Gifts
  addGiftAllocation: (itineraryId, gift) => axios.post(`${API_URL}/itineraries/${itineraryId}/gifts`, gift, { headers: getHeaders() }),
  updateGifts: (itineraryId, gifts) => axios.put(`${API_URL}/itineraries/${itineraryId}/gifts`, gifts, { headers: getHeaders() }),
  
  // Itinerary - AI Suggestions
  getAISuggestions: (itineraryId) => axios.get(`${API_URL}/itineraries/${itineraryId}/suggestions`, { headers: getHeaders() }),

  // RFPs
  getRFPs: () => axios.get(`${API_URL}/rfps`, { headers: getHeaders() }),
  createRFP: (data) => axios.post(`${API_URL}/rfps`, data, { headers: getHeaders() }),

  // Bids & Negotiations
  createBid: (data) => axios.post(`${API_URL}/bids`, data, { headers: getHeaders() }),
  getRFPBids: (rfpId) => axios.get(`${API_URL}/rfps/${rfpId}/bids`, { headers: getHeaders() }),
  getVendorBids: () => axios.get(`${API_URL}/vendor/bids`, { headers: getHeaders() }),
  updateBidStatus: (bidId, status) => axios.patch(`${API_URL}/bids/${bidId}/status?status=${status}`, {}, { headers: getHeaders() }),
  
  createNegotiation: (data) => axios.post(`${API_URL}/negotiations`, data, { headers: getHeaders() }),
  getBidNegotiations: (bidId) => axios.get(`${API_URL}/bids/${bidId}/negotiations`, { headers: getHeaders() }),
  
  createApproval: (data) => axios.post(`${API_URL}/approvals`, data, { headers: getHeaders() }),
  getApprovals: () => axios.get(`${API_URL}/approvals`, { headers: getHeaders() }),
  updateApproval: (approvalId, status, rejectionReason) => axios.patch(`${API_URL}/approvals/${approvalId}?status=${status}${rejectionReason ? `&rejection_reason=${rejectionReason}` : ''}`, {}, { headers: getHeaders() }),

  // Quotes
  getQuotes: (params) => axios.get(`${API_URL}/quotes`, { params, headers: getHeaders() }),
  createQuote: (data) => axios.post(`${API_URL}/quotes`, data, { headers: getHeaders() }),

  // Loyalty
  getLoyaltyBalance: () => axios.get(`${API_URL}/loyalty/balance`, { headers: getHeaders() }),
  getLoyaltyHistory: () => axios.get(`${API_URL}/loyalty/history`, { headers: getHeaders() }),

  // Newsletter
  subscribeNewsletter: (data) => axios.post(`${API_URL}/newsletter/subscribe`, data),

  // Admin
  getAdminStats: () => axios.get(`${API_URL}/admin/stats`, { headers: getHeaders() }),
  getAllUsers: () => axios.get(`${API_URL}/admin/users`, { headers: getHeaders() }),
  approveVendor: (vendorId, approved) => axios.patch(`${API_URL}/admin/vendors/${vendorId}/approve?approved=${approved}`, {}, { headers: getHeaders() }),
  
  // Admin Order Tracking
  getAdminGiftOrders: () => axios.get(`${API_URL}/admin/gift-orders`, { headers: getHeaders() }),
  getAdminCustomizedGiftOrders: () => axios.get(`${API_URL}/admin/customized-gift-orders`, { headers: getHeaders() }),
  getAdminTrainerBookings: () => axios.get(`${API_URL}/admin/trainer-bookings`, { headers: getHeaders() }),
  getAdminPaymentTransactions: () => axios.get(`${API_URL}/admin/payment-transactions`, { headers: getHeaders() }),

  // Vendor
  getVendorStats: () => axios.get(`${API_URL}/vendor/stats`, { headers: getHeaders() }),
  
  // Vendor API Management
  generateVendorApiKey: () => axios.post(`${API_URL}/vendor/api-key/generate`, {}, { headers: getHeaders() }),
  getVendorApiKeys: () => axios.get(`${API_URL}/vendor/api-keys`, { headers: getHeaders() }),
  revokeVendorApiKey: (keyId) => axios.delete(`${API_URL}/vendor/api-key/${keyId}`, { headers: getHeaders() }),
  configureVendorWebhook: (data) => axios.post(`${API_URL}/vendor/webhooks`, data, { headers: getHeaders() }),
  getVendorWebhooks: () => axios.get(`${API_URL}/vendor/webhooks`, { headers: getHeaders() }),

  // Bulk RFP
  createBulkRFP: (data) => axios.post(`${API_URL}/bulk-rfp`, data, { headers: getHeaders() }),
  getBulkRFPs: (params) => axios.get(`${API_URL}/bulk-rfp`, { params, headers: getHeaders() }),
  getBulkRFPDetail: (rfpId) => axios.get(`${API_URL}/bulk-rfp/${rfpId}`, { headers: getHeaders() }),
  getRFPPitches: (rfpId) => axios.get(`${API_URL}/bulk-rfp/${rfpId}/pitches`, { headers: getHeaders() }),
  submitPitch: (rfpId, data) => axios.post(`${API_URL}/bulk-rfp/${rfpId}/pitch`, data, { headers: getHeaders() }),
  updatePitchStatus: (pitchId, status) => axios.patch(`${API_URL}/pitches/${pitchId}/status?status=${status}`, {}, { headers: getHeaders() }),
  getVendorPitches: () => axios.get(`${API_URL}/vendor/pitches`, { headers: getHeaders() }),

  // Hotel Inventory
  createRoomInventory: (data) => axios.post(`${API_URL}/inventory/rooms`, data, { headers: getHeaders() }),
  bulkUpdateRoomInventory: (params) => axios.post(`${API_URL}/inventory/rooms/bulk`, null, { params, headers: getHeaders() }),
  getRoomInventory: (propertyId, params) => axios.get(`${API_URL}/inventory/rooms/${propertyId}`, { params, headers: getHeaders() }),
  createVenueSlot: (data) => axios.post(`${API_URL}/inventory/venues`, data, { headers: getHeaders() }),
  getVenueInventory: (propertyId, params) => axios.get(`${API_URL}/inventory/venues/${propertyId}`, { params, headers: getHeaders() }),
  createBlackoutDates: (data) => axios.post(`${API_URL}/inventory/blackout`, data, { headers: getHeaders() }),
  getBlackoutDates: (propertyId) => axios.get(`${API_URL}/inventory/blackout/${propertyId}`, { headers: getHeaders() }),
  checkAvailability: (propertyId, params) => axios.get(`${API_URL}/availability/check/${propertyId}`, { params, headers: getHeaders() }),

  // AI Chat
  sendAIChat: (data) => axios.post(`${API_URL}/chat/ai`, data, { headers: getHeaders() }),

  // Vendor Chat
  sendVendorChat: (data) => axios.post(`${API_URL}/chat/vendor`, data, { headers: getHeaders() }),
  getVendorChat: (recipientId) => axios.get(`${API_URL}/chat/vendor/${recipientId}`, { headers: getHeaders() }),

  // Public Data
  getBookingFeed: () => axios.get(`${API_URL}/public/booking-feed`),
  getApprovedTestimonials: () => axios.get(`${API_URL}/testimonials/approved`),
  getPlatformStats: () => axios.get(`${API_URL}/public/stats`),

  // Testimonials
  submitTestimonial: (data) => axios.post(`${API_URL}/testimonials`, data, { headers: getHeaders() }),
  getAdminTestimonials: () => axios.get(`${API_URL}/admin/testimonials`, { headers: getHeaders() }),
  updateTestimonialStatus: (id, status) => axios.patch(`${API_URL}/admin/testimonials/${id}?status=${status}`, {}, { headers: getHeaders() }),

  // 5-Star Hotels
  get5StarHotels: (params) => axios.get(`${API_URL}/hotels/5star`, { params }),
  getHotelCities: () => axios.get(`${API_URL}/hotels/cities`),
  getHotelChains: () => axios.get(`${API_URL}/hotels/chains`),

  // Hotel Invitations
  inviteHotel: (data) => axios.post(`${API_URL}/admin/invite-hotel`, data, { headers: getHeaders() }),
  bulkInviteHotels: (data) => axios.post(`${API_URL}/admin/invite-hotels/bulk`, data, { headers: getHeaders() }),
  getInvitations: (params) => axios.get(`${API_URL}/admin/invitations`, { params, headers: getHeaders() }),
  verifyInvitationCode: (code) => axios.get(`${API_URL}/verify-invitation/${code}`),
  markInvitationSent: (id) => axios.patch(`${API_URL}/admin/invitations/${id}/mark-sent`, {}, { headers: getHeaders() }),

  // Element Sheet
  getElementsCatalog: (category) => axios.get(`${API_URL}/elements/catalog`, { params: { category } }),

  // Admin - Business Protection
  getViolations: () => axios.get(`${API_URL}/admin/violations`, { headers: getHeaders() }),
  getPlatformHealth: () => axios.get(`${API_URL}/admin/platform-health`, { headers: getHeaders() }),
  getClientAnalytics: (clientId) => axios.get(`${API_URL}/admin/client-analytics/${clientId}`, { headers: getHeaders() }),

  // Event Permissions
  getRequiredPermissions: (params) => axios.post(`${API_URL}/permissions/required`, params, { headers: getHeaders() }),
  applyPermission: (data) => axios.post(`${API_URL}/permissions/apply`, data, { headers: getHeaders() }),
  getBookingPermissions: (bookingId) => axios.get(`${API_URL}/bookings/${bookingId}/permissions`, { headers: getHeaders() }),
  updatePermissionStatus: (permissionId, data) => axios.patch(`${API_URL}/permissions/${permissionId}/status`, data, { headers: getHeaders() }),
  getPermissionOpportunities: (params) => axios.get(`${API_URL}/permissions/opportunities`, { params, headers: getHeaders() }),
  getElementCategories: () => axios.get(`${API_URL}/elements/categories`),
  createElementSheet: (data) => axios.post(`${API_URL}/element-sheet`, data, { headers: getHeaders() }),
  getElementSheets: () => axios.get(`${API_URL}/element-sheets`, { headers: getHeaders() }),
  getElementSheet: (id) => axios.get(`${API_URL}/element-sheet/${id}`, { headers: getHeaders() }),
  updateElementSheet: (id, data) => axios.put(`${API_URL}/element-sheet/${id}`, data, { headers: getHeaders() }),
  updateElementSheetStatus: (id, status) => axios.patch(`${API_URL}/element-sheet/${id}/status?status=${status}`, {}, { headers: getHeaders() }),

  // Invitation Cards
  getInvitationTemplates: () => axios.get(`${API_URL}/invitation-templates`),
  getInvitationTemplate: (id) => axios.get(`${API_URL}/invitation-templates/${id}`),
  createInvitation: (data) => axios.post(`${API_URL}/invitations`, data, { headers: getHeaders() }),
  getInvitations: () => axios.get(`${API_URL}/invitations`, { headers: getHeaders() }),
  getInvitation: (id) => axios.get(`${API_URL}/invitations/${id}`, { headers: getHeaders() }),
  updateInvitation: (id, data) => axios.put(`${API_URL}/invitations/${id}`, data, { headers: getHeaders() }),
  deleteInvitation: (id) => axios.delete(`${API_URL}/invitations/${id}`, { headers: getHeaders() }),
  addInvitationRecipients: (id, data) => axios.post(`${API_URL}/invitations/${id}/recipients`, data, { headers: getHeaders() }),
  removeInvitationRecipient: (id, email) => axios.delete(`${API_URL}/invitations/${id}/recipients/${encodeURIComponent(email)}`, { headers: getHeaders() }),
  sendInvitation: (id, data) => axios.post(`${API_URL}/invitations/${id}/send`, data, { headers: getHeaders() }),
  parseCSVRecipients: (formData) => axios.post(`${API_URL}/invitations/parse-csv`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }),
  getInvitationFromBooking: (bookingId) => axios.get(`${API_URL}/invitations/from-booking/${bookingId}`, { headers: getHeaders() }),
};
